import { Table } from 'reactstrap';
import Wheelhouse from '../marinetools/components/wheelhouse/containers/Wheelhouse.jsx';
import WeatherObsExercises from '../marinetools/components/weatherobs/containers/WeatherObsExercises.jsx';
import weatherObsExercises from '../data/weatherObsExercises.js';

// Per-code traffic-light scale (blue -> green -> yellow -> orange -> red), matching the
// live allships.com.au Beaufort table. Text color picked per swatch via WCAG relative luminance.
const beaufortSwatches = {
  1: { bg: '#d0e9fb', text: '#0b1f33' },
  2: { bg: '#8cc8f2', text: '#0b1f33' },
  3: { bg: '#75cfd7', text: '#0b1f33' },
  4: { bg: '#61d1a2', text: '#0b1f33' },
  5: { bg: '#2bca7a', text: '#0b1f33' },
  6: { bg: '#a7dd3c', text: '#0b1f33' },
  7: { bg: '#ffec1a', text: '#0b1f33' },
  8: { bg: '#f9b006', text: '#0b1f33' },
  9: { bg: '#f2930d', text: '#0b1f33' },
  10: { bg: '#ec6d13', text: '#fff' },
  11: { bg: '#e24d36', text: '#fff' },
  12: { bg: '#bd2828', text: '#fff' },
};

const beaufortScale = [
  ['0', 'Calm', '0kts', '0m', 'Like a mirror'],
  ['1', 'Light Air', '1-3kts', '0 – 0.3m', 'Ripples with appearance of scales are formed, without foam crests'],
  ['2', 'Light Breeze', '4-6kts', '0.3 – 0.6m', 'Small wavelets still short but more pronounced; crests have a glassy appearance but do not break'],
  ['3', 'Gentle Breeze', '7-10kts', '0.6 – 1.2m', 'Large wavelets, crest begin to break;'],
  ['4', 'Moderate Breeze', '11-15kts', '1 – 2m', 'Small waves becoming longer and fairly frequent white horses'],
  ['5', 'Fresh Breeze', '16 – 20kts', '2 – 3m', 'Moderate waves taking a more pronounced long form. Many white horses are formed. Chance of some spray.'],
  ['6', 'Strong Breeze', '21-26kts', '3-4m', 'Large waves begin to form. The white foam crests are more extensive everywhere. Probably some spray.'],
  ['7', 'Near Gale', '27-33kts', '4 – 5.5m', 'Sea heaps up and white foam from breaking waves begins to be blown in streaks along the direction of the wind. Spindrift begins to be seen.'],
  ['8', 'Gale', '34-40kts', '5.5 – 7m', 'Moderately high waves of greater length. Edges of crests break into spindrift. Foam is blown in well-marked streaks along the direction of the wind.'],
  ['9', 'Strong Gale', '41-47kts', '7 – 10m', 'High waves. Dense streaks of foam along the direction of the wind. Sea begins to roll. Spray affects visibility.'],
  ['10', 'Storm', '48-55kts', '10 – 12.5m', 'Very high waves with long overhanging crests. Resulting foam in great patches is blown in dense white streaks along the direction of the wind. On the whole the surface of the sea takes on a white appearance. Rolling of the sea becomes heavy. Visibility affected.'],
  ['11', 'Violent Storm', '56-65kts', '12.5 – 14m', 'Exceptionally high waves. Small- and medium-sized ships might be for a long time lost to view behind the waves. Sea is covered with long white patches of foam. Everywhere the edges of the wave crests are blown into foam. Visibility affected.'],
  ['12', 'Hurricane', '> 65kts', '>14m', 'The air is filled with foam and spray. Sea is completely white with driving spray. Visibility very seriously affected.'],
];

function BeaufortCodeBadge({ code }) {
  const swatch = beaufortSwatches[code];
  if (!swatch) return <span>{code}</span>;
  return (
    <span className="as-beaufort-badge" style={{ background: swatch.bg, color: swatch.text }}>
      {code}
    </span>
  );
}

const weatherCodes = [
  ['a', 'Active'], ['b', 'Blue Sky (<25% clouded)'], ['c', 'Cloudy (>75% clouded)'],
  ['bc', 'Partly Cloudy (Combining b and c)'], ['d', 'Drizzling Rain'], ['f', 'Fog'],
  ['g', 'Dark and gloomy'], ['h', 'Hail'], ['l', 'Lightning'], ['o', 'Overcast'],
  ['p', 'Passing Showers'], ['q', 'Squally'], ['r', 'Rain'], ['s', 'Snow'],
  ['t', 'Thunder'], ['u', 'Ugly (Threatening)'], ['v', 'Visibility (Unusual Transparency)'],
];

const visibilityCodes = [
  ['0', 'Dense Fog', '< 50m'],
  ['1', 'Thick Fog', '50 – 200m'],
  ['2', 'Fog', '200 – 500m'],
  ['3', 'Moderate Fog', '500 – 1000m'],
  ['4', 'Mist or Thin Fog', '1000 – 2000m'],
  ['5', 'Poor Visibility', '2 – 4 km'],
  ['6', 'Moderate Visibility', '4 – 10km'],
  ['7', 'Good Visibility', '10 – 20km'],
  ['8', 'Very Good Visibility', '20 – 50km'],
  ['9', 'Exceptional Visibility', '> 50km'],
];

const seaStateCodes = [
  ['0', 'Calm – Glassy', '0m'],
  ['1', 'Calm – Rippled', '0 – 0.1m'],
  ['2', 'Smooth – Wavelets', '0.1 – 0.5m'],
  ['3', 'Slight', '0.5 – 1.25m'],
  ['4', 'Moderate', '1.25 – 2.5m'],
  ['5', 'Rough', '2.5 – 4m'],
  ['6', 'Very Rough', '4 – 6m'],
  ['7', 'High', '6 – 9m'],
  ['8', 'Very High', '9 – 14m'],
  ['9', 'Phenomenal', '> 14m'],
];

const windDirections = [
  ['N', 'North Wind', '0° / 360°'], ['NNE', 'North North East', '22.5°'],
  ['NE', 'North East', '45°'], ['ENE', 'East North East', '67.5°'],
  ['E', 'East Wind', '90°'], ['ESE', 'East South East', '112.5°'],
  ['SE', 'South East Wind', '135°'], ['SSE', 'South South East', '157.5°'],
  ['S', 'South Wind', '180°'], ['SSW', 'South South West', '202.5°'],
  ['SW', 'South West Wind', '225°'], ['WSW', 'West South West', '247.5°'],
  ['W', 'West Wind', '270°'], ['WNW', 'West North West', '292.5°'],
  ['NW', 'North West Wind', '315°'], ['NNW', 'North North West', '337.5°'],
];

export default function WeatherObservationCodes() {
  return (
    <section className="as-section">
      <div className="container">
        <h1 className="as-page-title">Weather Observation Codes</h1>
        <p className="as-lead">
          Weather Observation Codes are used to determine the state of the sea and
          weather for the purpose of recording it consistently in log books and over
          radio. They provide an abbreviated method of describing conditions from visual
          observations. This page describes 4 different scales to denote wind, sea
          disturbance, visibility and state of weather.
        </p>

        <h2 className="as-subheading">Beaufort Wind Scale</h2>
        <p>
          The Beaufort Scale is an empirical measure that relates wind speed to observed
          conditions at sea or on land. Its full name is the Beaufort wind force scale.
          The scale was devised in 1805 by the Irish hydrographer Francis Beaufort, a
          Royal Navy officer while serving on HMS Woolwich. The scale that carries
          Beaufort&rsquo;s name had a long and complex evolution from the previous work of
          others to when Beaufort was a Hydrographer of the Navy in the 1830s when it was
          adopted officially and first used during the voyage of HMS Beagle.
        </p>
        <p>
          In the 18th century, naval officers made regular weather observations, but
          there was no standard scale and so they could be very subjective. Beaufort
          succeeded in standardising a scale through visual observations.
        </p>
        <p>Below is a table showing the Beaufort Scale with speeds in knots and wave heights in metres.</p>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead>
            <tr><th>Code</th><th>Description</th><th>Wind Speed (Kts)</th><th>Wave Height (m)</th><th>Sea Conditions</th></tr>
          </thead>
          <tbody>
            {beaufortScale.map(([code, description, windSpeed, waveHeight, seaConditions]) => (
              <tr key={code}>
                <td><BeaufortCodeBadge code={code} /></td>
                <td>{description}</td>
                <td>{windSpeed}</td>
                <td>{waveHeight}</td>
                <td>{seaConditions}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <h2 className="as-subheading">State of Weather Codes</h2>
        <p>
          Beaufort Notation to indicate the State of the Weather. In this scale the
          weather designations could be combined, and reported, for example, as
          &ldquo;s.c.&rdquo; for snow and detached cloud or &ldquo;g.r.q.&rdquo; for dark,
          rain and squally.
        </p>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Code</th><th>Weather</th></tr></thead>
          <tbody>
            {weatherCodes.map((row) => (
              <tr key={row[0]}><td>{row[0]}</td><td>{row[1]}</td></tr>
            ))}
          </tbody>
        </Table>
        <p>To denote Partly Cloudy, combine two codes Blue Sky (b) and Cloudy (c) to: bc</p>

        <h2 className="as-subheading">Visibility Codes</h2>
        <p>The international standards for describing visibility in marine forecasts in metric ranges are as follows:</p>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Code</th><th>Description</th><th>Distance of Vision</th></tr></thead>
          <tbody>
            {visibilityCodes.map((row) => (
              <tr key={row[0]}>{row.map((cell, i) => <td key={i}>{cell}</td>)}</tr>
            ))}
          </tbody>
        </Table>

        <h2 className="as-subheading">Sea State Disturbance Codes</h2>
        <p>
          The Sea Disturbance Code refers to the World Meteorological Organization (WMO)
          sea state code, a system used to describe the state of the sea surface based on
          wave height and period. It is the vertical distance between the crest and the
          trough of a wave. The code provides a standardised way to communicate sea
          conditions between mariners and weather forecasters.
        </p>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Code</th><th>Description</th><th>Height of Wave (Crest to Trough)</th></tr></thead>
          <tbody>
            {seaStateCodes.map((row) => (
              <tr key={row[0]}>{row.map((cell, i) => <td key={i}>{cell}</td>)}</tr>
            ))}
          </tbody>
        </Table>

        <h2 className="as-subheading">Wind Direction Short Codes</h2>
        <p>
          Wind direction is generally reported by the direction from which the wind
          originates. For example, a north or northerly wind blows from the north to the
          south. The exceptions are onshore winds (blowing onto the shore from the water)
          and offshore winds (blowing off the shore to the water). Wind direction is
          usually reported in cardinal (or compass) direction, or in degrees.
        </p>
        <p>
          Wind direction can be described using short codes or abbreviations. Common
          short codes include: N (North), E (East), S (South), W (West), NE
          (Northeast), SE (Southeast), SW (Southwest), and NW (Northwest). These codes
          are often used on weather maps and in weather reports.
        </p>
        <ul>
          <li><strong>Cardinal Directions/Principal Winds:</strong> N, E, S, W represent North, East, South, and West, respectively.</li>
          <li><strong>Intercardinal Directions/Half Winds:</strong> NE, SE, SW, and NW represent Northeast, Southeast, Southwest, and Northwest, respectively.</li>
          <li><strong>Secondary Intercardinal Directions/Quarter Winds:</strong> north-northeast (NNE), east-northeast (ENE), east-southeast (ESE), south-southeast (SSE), south-southwest (SSW), west-southwest (WSW), west-northwest (WNW), north-northwest (NNW).</li>
        </ul>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Direction Short Code</th><th>Direction Description</th><th>Degrees</th></tr></thead>
          <tbody>
            {windDirections.map((row) => (
              <tr key={row[0]}>{row.map((cell, i) => <td key={i}>{cell}</td>)}</tr>
            ))}
          </tbody>
        </Table>

        <h2 className="as-subheading">Progress Check</h2>
        <p>
          As Deck Officer on M/S Mary Jane, you are responsible for keeping the
          ship&rsquo;s log updated every hour with weather, sea and visibility
          conditions. You also need to record other information about the voyage such as
          vessel speed, heading and distance travelled in nautical miles. Use the
          description of visualisations in each tab exercise together with readings from
          your Wheelhouse instruments.
        </p>
        <p>A couple of notes on the log columns:</p>
        <ul>
          <li>
            <strong>Log Time:</strong> The 24-hour clock is used to record time entries,
            particularly for events like departure, arrival, and radio communications. It
            ensures consistent timekeeping across different locations and simplifies
            calculations, especially when dealing with time zones and international
            regulations. For example 06:00 is equivalent of 6am and 13:00 is the same as
            1pm.
          </li>
          <li>
            <strong>Compass Heading:</strong> The vessel compass is magnetic. It works by
            using the Earth&rsquo;s magnetic field to determine direction. Specifically,
            it contains a magnetized needle that aligns itself with the Earth&rsquo;s
            magnetic north. This is in contrast to GPS devices which typically display
            headings and bearings in True North. Logs may vary on vessels with some using
            True or Magnetic. Some additionally record the compass error (Deviation) in
            the log. M/S Mary Jane records Magnetic only so make sure you use the compass
            value when recording heading in below exercises.
          </li>
          <li>
            <strong>Log Distance:</strong> This is the distance travelled in nautical
            miles (nm) since your last log recording. Use the time difference between
            previous log entry to current time in combination with vessel speed. Vessel
            speed or Speed Over Ground is measured in Knots (kts) which is nautical miles
            travelled per hour. If your vessel is cruising at 12 kts for an hour, you have
            travelled 12nm.
          </li>
          <li><strong>Total Distance:</strong> Is the accumulated distance travelled since first log entry.</li>
        </ul>

        <Wheelhouse />
        <WeatherObsExercises data={weatherObsExercises} />
      </div>
    </section>
  );
}
